import { useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import { IDoesFilterPassParams } from 'ag-grid-community'
import { CustomFilterProps, useGridFilter } from 'ag-grid-react'
import { APIResponse } from '../../../../../../lib/commons/api'
import { BulkSheetFilter, FilterFooter } from '../common'
import { Checkbox, Typography } from '@mui/material'
import {
  CheckboxArea,
  FilterLabel,
  SelectFilter,
  SelectFilterArea,
  SelectFilterFormControl,
} from '../SelectFilter/index'
import { WbsItemIcon } from '../../../../../components/icons/WbsItemIcon'
import { useSelector } from 'react-redux'
import { AllState } from '../../../../../../store'
import usePrevious from '../../../../../hooks/usePrevious'
import { WbsItemTypeVO } from '../../../../../../domain/value-object/WbsItemTypeVO'

type WbsItemTypeFilterModel = {
  values: string[]
}

type Props = CustomFilterProps & {
  fetch: (v: SelectFilter) => Promise<APIResponse>
  wbsItemType: WbsItemTypeVO[]
}

const buildWbsItemTypeFilterModel = (
  values: string[],
  allOptionValues: string[]
): WbsItemTypeFilterModel | null => {
  const isAllSelected =
    allOptionValues.length > 0 && values.length === allOptionValues.length
  return isAllSelected ? null : { values }
}

export const ServerSideWbsItemTypeFilter = ({
  model,
  onModelChange,
  api,
  fetch: fetchFn,
  context,
}: Props) => {
  const hasRequiredSaveData = useSelector<AllState>(
    state => state.hasRequiredSaveData.hasRequiredSaveData
  )
  const prevHasRequiredSaveData = usePrevious(hasRequiredSaveData)

  // Options from context
  const wbsItemTypes: WbsItemTypeVO[] = context['wbsItemType'] ?? []
  const allOptionValues = wbsItemTypes.map(v => v.baseTicketUuid ?? v.uuid)

  // Derive state from model (single source of truth)
  const values =
    (model as WbsItemTypeFilterModel | null)?.values ?? allOptionValues
  const isAllSelected = !model || values.length === allOptionValues.length
  const isAllDeselected = model !== null && values.length === 0

  // UI-local state for async filtering
  const [loading, isLoading] = useState(false)
  const [filteredIds, setFilteredIds] = useState<Set<string> | undefined>(
    undefined
  )

  const addedRowIds = useRef<string[]>()
  useEffect(() => {
    if (addedRowIds.current) return
    addedRowIds.current = []
  }, [])

  const fetchFilteredIds = async (v: SelectFilter) => {
    const response = await fetchFn(v)
    setFilteredIds(new Set(response.json))
  }

  const search = useMemo(
    () =>
      _.debounce(async (v: SelectFilter | null) => {
        if (!v) {
          setFilteredIds(undefined)
          return
        }
        if (v.values.length === 0) {
          setFilteredIds(new Set())
          return
        }
        try {
          isLoading(true)
          await fetchFilteredIds(v)
        } finally {
          isLoading(false)
        }
      }, 300),
    []
  )

  // When criteria changes (model changes), trigger debounced search
  const modelKey = JSON.stringify(model)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- modelKey は model の値比較用。model 自体は毎レンダーで新規オブジェクトになるため deps に入れると毎回発火する。search は useMemo で安定
  useEffect(() => {
    // WbsItemTypeFilter の fetch は SelectFilter 型（values + includeBlank）を期待する
    const filterModel = model as WbsItemTypeFilterModel | null
    if (!filterModel) {
      search(null)
    } else {
      search({ values: filterModel.values, includeBlank: false })
    }
  }, [modelKey])

  // When filteredIds changes, trigger re-filtering
  // eslint-disable-next-line react-hooks/exhaustive-deps -- api は AG Grid から渡される安定参照。filteredIds 変更時のみ再フィルタリングをトリガーする
  useEffect(() => {
    api.onFilterChanged()
  }, [filteredIds])

  // Re-fetch after save
  // eslint-disable-next-line react-hooks/exhaustive-deps -- hasRequiredSaveData の変化のみをトリガーとし、その時点の最新 model/fetchFilteredIds を読む
  useEffect(() => {
    const isAfterSavedOrRefreshed =
      prevHasRequiredSaveData && !hasRequiredSaveData
    if (isAfterSavedOrRefreshed && model) {
      const filterModel = model as WbsItemTypeFilterModel
      fetchFilteredIds({
        values: filterModel.values,
        includeBlank: false,
      }).then(() => {
        addedRowIds.current = []
      })
    }
  }, [hasRequiredSaveData])

  useGridFilter({
    doesFilterPass(params: IDoesFilterPassParams) {
      // Don't filter new rows
      if (params.data.added) {
        if (addedRowIds.current && params.node.id) {
          addedRowIds.current.push(params.node.id)
        }
        return true
      }
      if (
        addedRowIds.current &&
        params.node.id &&
        addedRowIds.current.includes(params.node.id)
      ) {
        return true
      }
      if (!filteredIds) return true
      if (isAllDeselected) return false
      return params.node.id ? filteredIds.has(params.node.id) : false
    },

    getModelAsString() {
      if (!model) return ''
      return `${wbsItemTypes
        .filter(o => values.includes(o.baseTicketUuid ?? o.uuid))
        .map(o => o.getNameWithSuffix())
        .join(',')}`
    },
  })

  // Helpers for model updates
  const updateValues = (newValues: string[]) => {
    onModelChange(buildWbsItemTypeFilterModel(newValues, allOptionValues))
  }

  return (
    <BulkSheetFilter>
      <SelectFilterArea>
        <SelectFilterFormControl
          label="(Select All)"
          control={
            <Checkbox
              checked={isAllSelected}
              indeterminate={!isAllSelected && !isAllDeselected}
              onChange={e =>
                e.target.checked
                  ? onModelChange(null)
                  : onModelChange({ values: [] })
              }
              disableRipple={true}
            />
          }
        />
        <CheckboxArea>
          {wbsItemTypes.map(option => {
            const optionValue = option.baseTicketUuid ?? option.uuid
            return (
              <SelectFilterFormControl
                key={`type-select-filter-${option.code}`}
                label={
                  <FilterLabel>
                    <WbsItemIcon type={option} />
                    <Typography sx={{ padding: '0 3px' }}>
                      {option.getNameWithSuffix()}
                    </Typography>
                  </FilterLabel>
                }
                control={
                  <Checkbox
                    checked={values.includes(optionValue)}
                    onChange={e => {
                      if (e.target.checked) {
                        updateValues([...values, optionValue])
                      } else {
                        updateValues(values.filter(v => v !== optionValue))
                      }
                    }}
                    disableRipple={true}
                  />
                }
              />
            )
          })}
        </CheckboxArea>
      </SelectFilterArea>
      <FilterFooter loading={loading} onClick={() => onModelChange(null)} />
    </BulkSheetFilter>
  )
}
